import {
  DateField,
  ExportButton,
  List,
  RefreshButton,
  SaveButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  FormProps,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CrudFilters,
  IResourceComponentsProps,
  getDefaultFilter,
  useExport,
} from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import React, { useMemo } from "react";
import dayjs from "dayjs";
import { IVesselDataVerificationListItem, IVessel, IShipco } from "interfaces";
import { CopyableEmailTag } from "components/fields/CopyableEmailTag";
import { VerificationStatusTag } from "./components/VerificationStatusTag";

const { RangePicker } = DatePicker;

export const VesselDataVerificationsList: React.FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "id",
    pagination: { mode: "server" },
  });

  const { selectProps: shipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "id",
    pagination: { mode: "server" },
  });

  const {
    tableProps,
    sorters,
    filters,
    tableQuery: { isLoading, refetch },
    searchFormProps,
  } = useTable<IVesselDataVerificationListItem>({
    resource: "vessel-data-verifications",
    syncWithLocation: true,
    pagination: { pageSize: 50, mode: "server" },
    sorters: { initial: [{ field: "sentAt", order: "desc" }] },
    filters: { defaultBehavior: "replace" },
    onSearch: (params: any) => {
      const f: CrudFilters = [];
      const { status, vesselId, shipcoId, dateRange } = params;
      f.push({ field: "status",   operator: "eq",  value: status   || undefined });
      f.push({ field: "vesselId", operator: "eq",  value: vesselId || undefined });
      f.push({ field: "shipcoId", operator: "eq",  value: shipcoId || undefined });
      if (dateRange && dateRange[0] && dateRange[1]) {
        f.push({ field: "sentAt", operator: "gte", value: dateRange[0].startOf("day").toISOString() });
        f.push({ field: "sentAt", operator: "lte", value: dateRange[1].endOf("day").toISOString() });
      }
      return f;
    },
  });

  const dateRangeDefault = useMemo(() => {
    const start = getDefaultFilter("sentAt", filters || [], "gte");
    const end   = getDefaultFilter("sentAt", filters || [], "lte");
    return start && end ? [dayjs(start as string), dayjs(end as string)] as [any, any] : undefined;
  }, [filters]);

  const { triggerExport, isLoading: exportLoading } = useExport<IVesselDataVerificationListItem>({
    resource: "vessel-data-verifications",
    filters: filters || [],
    sorters: sorters || [],
    filename: `vessel_data_verifications_${new Date().toISOString().slice(0, 10)}`,
    mapData: (item) => ({
      "Vessel":         item.vesselName,
      "IMO":            item.imoNumber,
      "Shipco":         item.shipcoName,
      "Status":         item.status,
      "Sent At":        item.sentAt,
      "Approver Email": item.approverEmail || "",
      "Approved At":    item.approvedAt || "",
      "Approve By":     item.expiresAt,
      "On Hold Reason": item.notApprovedReason || "",
      "On Hold By":     item.notApprovedByEmail || "",
      "On Hold At":     item.notApprovedAt || "",
      "Public ID":      item.publicId,
    }),
    unparseConfig: {
      header: true,
      columns: ["Vessel", "IMO", "Shipco", "Status", "Sent At", "Approver Email", "Approved At", "Approve By", "On Hold Reason", "On Hold By", "On Hold At", "Public ID"],
      escapeFormulae: true,
    },
  });

  const columns: ColumnsType<IVesselDataVerificationListItem> = [
    {
      title: "Vessel",
      dataIndex: "vesselName",
      key: "vesselName",
      width: 200,
      render: (v) => <TextField value={v} />,
    },
    {
      title: "IMO",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 120,
      render: (v) => <TextField value={v} />,
    },
    {
      title: "Shipco",
      dataIndex: "shipcoName",
      key: "shipcoName",
      width: 200,
      render: (v) => <TextField value={v} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (v) => <VerificationStatusTag status={v} />,
    },
    {
      title: "Sent",
      dataIndex: "sentAt",
      key: "sentAt",
      width: 170,
      sorter: true,
      defaultSortOrder: "descend",
      render: (v) => v ? <DateField value={v} format="DD-MMM-YYYY HH:mm" /> : <TextField value="—" />,
    },
    {
      title: "Approver",
      dataIndex: "approverEmail",
      key: "approverEmail",
      width: 220,
      render: (v) => v ? <CopyableEmailTag value={v} /> : <TextField value="—" />,
    },
    {
      title: "Approved",
      dataIndex: "approvedAt",
      key: "approvedAt",
      width: 170,
      render: (v) => v ? <DateField value={v} format="DD-MMM-YYYY HH:mm" /> : <TextField value="—" />,
    },
    {
      title: "Approve By",
      dataIndex: "expiresAt",
      key: "expiresAt",
      width: 170,
      render: (v) => v ? <DateField value={v} format="DD-MMM-YYYY HH:mm" /> : <TextField value="—" />,
    },
    {
      title: "On Hold Reason",
      dataIndex: "notApprovedReason",
      key: "notApprovedReason",
      width: 240,
      ellipsis: { showTitle: false },
      render: (v) =>
        v ? (
          <Tooltip title={v} placement="topLeft">
            <span>{v}</span>
          </Tooltip>
        ) : (
          <TextField value="—" />
        ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/vessel-verifications/${record.publicId}/approve`);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        title: "Vessel Data Verifications",
        subTitle: "approval status and history",
        tags: isLoading ? <Tag color="orange">loading</Tag> : <Tag color="green">live</Tag>,
        extra: (
          <Space>
            <RefreshButton onClick={() => refetch()} />
            <ExportButton onClick={triggerExport} loading={exportLoading} />
          </Space>
        ),
        ghost: true,
        footer: <Divider plain>© {new Date().getFullYear()} e-Mariner NavSolutions</Divider>,
      }}
    >
      <Row gutter={[16, 16]}>
        <Col xl={24} lg={24} xs={24}>
          <Card type="inner" title="Filter">
            <Filter
              formProps={searchFormProps}
              filters={filters || []}
              vesselSelectProps={vesselSelectProps}
              shipcoSelectProps={shipcoSelectProps}
              dateRangeDefault={dateRangeDefault}
            />
          </Card>
        </Col>
        <Col xl={24} xs={24}>
          <Table
            {...tableProps}
            columns={columns}
            rowKey="publicId"
            size="middle"
            scroll={{ x: 1540 }}
            sticky
            onRow={(record) => ({
              onClick: () => navigate(`/vessel-verifications/${record.publicId}/approve`),
              style: { cursor: "pointer" },
            })}
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            }}
          />
        </Col>
      </Row>
    </List>
  );
};

const Filter: React.FC<{
  formProps: FormProps;
  filters: CrudFilters;
  vesselSelectProps: any;
  shipcoSelectProps: any;
  dateRangeDefault?: [any, any];
}> = ({ formProps, filters, vesselSelectProps, shipcoSelectProps, dateRangeDefault }) => {
  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        status:    getDefaultFilter("status",   filters),
        vesselId:  getDefaultFilter("vesselId", filters),
        shipcoId:  getDefaultFilter("shipcoId", filters),
        dateRange: dateRangeDefault,
      }}
    >
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col xs={24} sm={24} md={5}>
          <Form.Item label="Status" name="status">
            <Select placeholder="filter by status" allowClear>
              <Select.Option value="pending">pending</Select.Option>
              <Select.Option value="approved">approved</Select.Option>
              <Select.Option value="not_approved">on hold</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item label="Vessel" name="vesselId">
            <Select
              {...vesselSelectProps}
              placeholder="filter by vessel"
              allowClear
              showSearch
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item label="Shipco" name="shipcoId">
            <Select
              {...shipcoSelectProps}
              placeholder="filter by shipco"
              allowClear
              showSearch
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={5}>
          <Form.Item label="Date Range (Sent)" name="dateRange">
            <RangePicker style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={2}>
          <Form.Item style={{ marginBottom: 0 }}>
            <SaveButton onClick={formProps.form?.submit} size="large" block>
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
